import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CurrencyCode, isCurrencyCode } from '../lib/currency';

const DOCUMENT_CURRENCY_STORAGE_KEY = 'soloos-document-currency';

interface CurrencyContextValue {
  documentCurrency: CurrencyCode;
  setDocumentCurrency: (currency: CurrencyCode) => void;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

function getStoredCurrency(key: string): CurrencyCode | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = window.localStorage.getItem(key);
  return isCurrencyCode(value) ? value : null;
}

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [documentCurrency, setDocumentCurrencyState] = useState<CurrencyCode>(() => {
    return getStoredCurrency(DOCUMENT_CURRENCY_STORAGE_KEY) || 'INR';
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(DOCUMENT_CURRENCY_STORAGE_KEY, documentCurrency);
  }, [documentCurrency]);

  const value = useMemo<CurrencyContextValue>(
    () => ({
      documentCurrency,
      setDocumentCurrency: setDocumentCurrencyState,
    }),
    [documentCurrency],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider.');
  }

  return context;
}
